import React from 'react';
import { Loader2, Pencil, Plus, Trash2, Check, X } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { _money, _monthLabel, _FREQ_CHOICES } from './utils';

export function Section3Grid({ months, rows, clients, services, dateRange, onChanged }) {
  const { toast } = useToast();
  const [drafts, setDrafts] = React.useState({}); // { id or 'new': {clientMode,clientId,clientName,siteName,serviceId,price,freq,customDays,start,notes} }
  const [savingId, setSavingId] = React.useState(null);
  const [showNewRow, setShowNewRow] = React.useState(false);

  const startEdit = (row) => {
    const isExistingClient = clients.some(c => c.name === row.client_name);
    setDrafts(d => ({ ...d, [row.id]: {
      clientMode: isExistingClient ? 'existing' : 'new',
      clientId: isExistingClient ? String(clients.find(c => c.name === row.client_name)?.id ?? '') : '',
      clientName: row.client_name || '',
      siteName: row.site_name || '',
      serviceId: row.service_id ? String(row.service_id) : '',
      price: row.price != null ? String(row.price) : '',
      freq: row.frequency || '',
      customDays: row.custom_days ?? '',
      start: row.start_date || dateRange?.start || '',
      notes: row.notes || '',
    }}));
  };
  const cancelEdit = (id) => {
    setDrafts(d => { const { [id]: _, ...rest } = d; return rest; });
    if (id === 'new') setShowNewRow(false);
  };
  const updateDraft = (id, patch) => setDrafts(d => ({ ...d, [id]: { ...d[id], ...patch } }));

  const persist = async (id) => {
    const draft = drafts[id];
    if (!draft) return;
    const clientName = draft.clientMode === 'existing'
      ? (clients.find(c => String(c.id) === draft.clientId)?.name || '')
      : (draft.clientName || '').trim();
    if (!clientName || !draft.siteName.trim()) {
      toast({ title: 'Client and site name required', variant: 'destructive' });
      return;
    }
    setSavingId(id);
    try {
      const body = {
        client_name: clientName,
        site_name: draft.siteName.trim(),
        service_id: draft.serviceId ? Number(draft.serviceId) : null,
        frequency: draft.freq || null,
        custom_days: draft.freq === 'custom' && draft.customDays ? Number(draft.customDays) : null,
        price: draft.price !== '' && draft.price != null ? Number(draft.price) : 0,
        start_date: draft.start || null,
        notes: draft.notes || null,
      };
      if (id === 'new') {
        await api.post('/reports/revenue-budget/new-business', body);
        setShowNewRow(false);
      } else {
        await api.patch(`/reports/revenue-budget/new-business/${id}`, body);
      }
      cancelEdit(id);
      toast({ title: 'Saved' });
      await onChanged();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSavingId(null); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.client_name} · ${row.site_name}" from the plan?`)) return;
    try {
      await api.delete(`/reports/revenue-budget/new-business/${row.id}`);
      toast({ title: 'Deleted' });
      await onChanged();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const addNewRow = () => {
    setShowNewRow(true);
    setDrafts(d => ({ ...d, new: {
      clientMode: 'existing', clientId: '', clientName: '',
      siteName: '', serviceId: '', price: '',
      freq: '', customDays: '', start: dateRange?.start || '', notes: '',
    }}));
  };

  const colSpan = 9 + months.length;

  // iter491 — Section 3 subtotal (per month + full year).
  const s3MonthTotals = React.useMemo(() => {
    const acc = {};
    months.forEach(m => { acc[m] = 0; });
    (rows || []).forEach(r => months.forEach(m => { acc[m] += Number(r.months?.[m] || 0); }));
    return acc;
  }, [rows, months]);
  const s3YearTotal = React.useMemo(() => (
    (rows || []).reduce((a, r) => a + Number(r.total || 0), 0)
  ), [rows]);

  const renderActionsCell = (id) => (
    <td className="px-1 py-1 border-b align-middle whitespace-nowrap sticky left-0 bg-white z-10 w-20 min-w-[80px]">
      <div className="flex items-center gap-0.5 justify-center">
        <button type="button" onClick={() => persist(id)} disabled={savingId === id}
          className="p-1 rounded hover:bg-emerald-100 text-emerald-700"
          title="Save"
          data-testid={`s3-save-${id}`}>
          {savingId === id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
        <button type="button" onClick={() => cancelEdit(id)} disabled={savingId === id}
          className="p-1 rounded hover:bg-slate-100 text-slate-500"
          title="Cancel"
          data-testid={`s3-cancel-${id}`}>
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </td>
  );

  const renderEditor = (id, draft) => (
    <>
      {renderActionsCell(id)}
      <td className="px-1 py-1 border-b align-top sticky left-20 bg-white z-10">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <select
              value={draft.clientMode}
              onChange={(e) => updateDraft(id, { clientMode: e.target.value })}
              className="text-[10px] border rounded px-1 py-0.5"
              data-testid={`s3-client-mode-${id}`}
            >
              <option value="existing">Existing client</option>
              <option value="new">New client</option>
            </select>
          </div>
          {draft.clientMode === 'existing' ? (
            <select
              value={draft.clientId}
              onChange={(e) => updateDraft(id, { clientId: e.target.value })}
              className="text-[11px] border rounded px-1 py-0.5 w-56"
              data-testid={`s3-client-select-${id}`}
            >
              <option value="">— pick a client —</option>
              {clients.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
            </select>
          ) : (
            <input
              type="text"
              value={draft.clientName}
              onChange={(e) => updateDraft(id, { clientName: e.target.value })}
              placeholder="New client name"
              className="text-[11px] border rounded px-1 py-0.5 w-56"
              data-testid={`s3-client-name-${id}`}
            />
          )}
          <input
            type="text"
            value={draft.siteName}
            onChange={(e) => updateDraft(id, { siteName: e.target.value })}
            placeholder="Site name"
            className="text-[11px] border rounded px-1 py-0.5 w-56"
            data-testid={`s3-site-name-${id}`}
          />
        </div>
      </td>
      <td className="px-1 py-1 border-b align-top">
        <select
          value={draft.serviceId}
          onChange={(e) => updateDraft(id, { serviceId: e.target.value })}
          className="text-[11px] border rounded px-1 py-0.5 w-full"
          data-testid={`s3-service-${id}`}
        >
          <option value="">— pick service —</option>
          {(services || []).map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
        </select>
      </td>
      <td className="px-1 py-1 border-b align-top">
        <select
          value={draft.freq}
          onChange={(e) => updateDraft(id, { freq: e.target.value })}
          className="text-[11px] border rounded px-1 py-0.5 w-full capitalize"
          data-testid={`s3-frequency-${id}`}
        >
          <option value="">— pick freq —</option>
          {_FREQ_CHOICES.filter(c => c.v !== '' && c.v !== '__none__' && c.v !== 'adhoc').map(c =>
            <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>
        {draft.freq === 'custom' && (
          <input type="number" placeholder="days"
            value={draft.customDays}
            onChange={(e) => updateDraft(id, { customDays: e.target.value })}
            className="text-[11px] border rounded px-1 py-0.5 w-full mt-1"
            data-testid={`s3-custom-days-${id}`}
          />
        )}
      </td>
      <td className="px-1 py-1 border-b align-top">
        <input type="date"
          value={draft.start}
          min={dateRange?.start}
          max={dateRange?.end}
          onChange={(e) => updateDraft(id, { start: e.target.value })}
          className="text-[11px] border rounded px-1 py-0.5 w-full"
          data-testid={`s3-start-date-${id}`}
        />
      </td>
      <td className="px-1 py-1 border-b text-right align-top">
        <input type="number" step="0.01"
          value={draft.price}
          onChange={(e) => updateDraft(id, { price: e.target.value })}
          placeholder="£"
          className="text-[11px] border rounded px-1 py-0.5 w-20 text-right"
          data-testid={`s3-price-${id}`}
        />
      </td>
      <td className="px-1 py-1 border-b align-top">
        <input type="text"
          value={draft.notes}
          onChange={(e) => updateDraft(id, { notes: e.target.value })}
          placeholder="Notes"
          className="text-[11px] border rounded px-1 py-0.5 w-full"
          data-testid={`s3-notes-${id}`}
        />
      </td>
      {months.map(m => <td key={m} className="border-b text-slate-300 text-right text-[11px]">—</td>)}
      <td className="border-b" />
    </>
  );

  return (
    <Card data-testid="revenue-budget-section3">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Section 3 — New Client / New Business</CardTitle>
        <Button size="sm" onClick={addNewRow} disabled={showNewRow} data-testid="add-new-business-btn">
          <Plus className="h-4 w-4 mr-1" /> Add row
        </Button>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-xs border-collapse min-w-[1200px]">
          <thead className="bg-slate-100">
            <tr>
              <th className="px-1 py-2 border-b sticky left-0 bg-slate-100 z-20 w-20 min-w-[80px]"></th>
              <th className="text-left px-2 py-2 border-b sticky left-20 bg-slate-100 z-20 min-w-[220px]">Client / Site</th>
              <th className="text-left px-1 py-2 border-b min-w-[150px] w-[160px]">Service</th>
              <th className="text-left px-1 py-2 border-b min-w-[120px] w-[130px]">Frequency</th>
              <th className="text-left px-1 py-2 border-b min-w-[120px] w-[130px]">Start</th>
              <th className="text-right px-1 py-2 border-b min-w-[80px] w-[90px]">£ / job</th>
              <th className="text-left px-1 py-2 border-b min-w-[130px] w-[150px]">Notes</th>
              {months.map(m => (
                <th key={m} className="text-right px-2 py-2 border-b whitespace-nowrap">{_monthLabel(m)}</th>
              ))}
              <th className="text-right px-2 py-2 border-b bg-slate-50 whitespace-nowrap">Total</th>
            </tr>
          </thead>
          <tbody>
            {showNewRow && (
              <tr className="bg-emerald-50/40" data-testid="s3-new-row">
                {renderEditor('new', drafts.new || {})}
              </tr>
            )}
            {rows.length === 0 && !showNewRow && (
              <tr><td colSpan={colSpan} className="text-center text-slate-500 py-6">
                No new-business lines yet. Click <strong>Add row</strong> to include jobs for a new client or upcoming business win.
              </td></tr>
            )}
            {rows.map(row => {
              const draft = drafts[row.id];
              if (draft) {
                return (
                  <tr key={row.id} className="bg-amber-50/40" data-testid={`s3-editing-${row.id}`}>
                    {renderEditor(row.id, draft)}
                  </tr>
                );
              }
              return (
                <tr key={row.id} className="hover:bg-slate-50/60">
                  <td className="border-b sticky left-0 bg-white z-10 whitespace-nowrap px-1 text-center">
                    <button type="button" onClick={() => startEdit(row)} className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-800 mr-1" title="Edit" data-testid={`edit-s3-${row.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" onClick={() => remove(row)} className="p-1 rounded hover:bg-rose-100 text-rose-600" title="Delete" data-testid={`delete-s3-${row.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                  <td className="px-2 py-1.5 border-b sticky left-20 bg-white z-10 whitespace-nowrap" data-testid={`s3-row-${row.id}`}>
                    <div className="font-medium">{row.client_name}</div>
                    <div className="text-slate-500 text-[11px]">{row.site_name}</div>
                  </td>
                  <td className="px-2 py-1.5 border-b whitespace-nowrap text-slate-600">{row.service_name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-1.5 border-b capitalize">{row.frequency || '—'}{row.custom_days ? ` (${row.custom_days}d)` : ''}</td>
                  <td className="px-2 py-1.5 border-b">{row.start_date || '—'}</td>
                  <td className="px-2 py-1.5 text-right border-b">{_money(row.price)}</td>
                  <td className="px-2 py-1.5 border-b text-slate-600">{row.notes || <span className="text-slate-400">—</span>}</td>
                  {months.map(m => (
                    <td key={m} className={`px-2 py-1.5 text-right border-b ${row.months?.[m] ? '' : 'text-slate-300'}`}>
                      {row.months?.[m] ? _money(row.months[m]) : '—'}
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right border-b bg-slate-50/40 font-medium">{_money(row.total)}</td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-100 border-t-2 border-slate-300 font-bold">
              <tr data-testid="revenue-budget-section3-subtotal-row">
                <td className="border-b sticky left-0 bg-slate-100 z-10" />
                <td className="px-2 py-2 border-b sticky left-20 bg-slate-100 z-10 whitespace-nowrap uppercase text-[11px] tracking-wide text-slate-700">
                  Section 3 total
                </td>
                <td className="border-b" colSpan={5} />
                {months.map(m => (
                  <td key={m} className={`px-2 py-2 text-right border-b whitespace-nowrap ${s3MonthTotals[m] ? '' : 'text-slate-400'}`}
                    data-testid={`revenue-budget-section3-subtotal-${m}`}>
                    {s3MonthTotals[m] ? _money(s3MonthTotals[m]) : '—'}
                  </td>
                ))}
                <td className="px-2 py-2 text-right border-b bg-slate-200 whitespace-nowrap"
                  data-testid="revenue-budget-section3-subtotal-year">{_money(s3YearTotal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </CardContent>
    </Card>
  );
}
